import React, { useState } from 'react';
import InputText from '../../../components/agv-forms-input';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function VehicleContainersStep({ vehicleData, setVehicleData }) {
  const isCiterneCompartimentee = vehicleData.LIBTYP === 'Citerne compartimentée';
  
  // Nombre de containers visibles (pour citerne compartimentée)
  const [visibleContainersCount, setVisibleContainersCount] = useState(1);

  const containers = [
    { key: 'VCASCA_01', label: 'Container 1', required: true },
    { key: 'VCASCA_02', label: 'Container 2', required: false },
    { key: 'VCASCA_03', label: 'Container 3', required: false },
    { key: 'VCASCA_04', label: 'Container 4', required: false },
    { key: 'VCASCA_05', label: 'Container 5', required: false },
    { key: 'VCASCA_06', label: 'Container 6', required: false },
    { key: 'VCASCA_07', label: 'Container 7', required: false },
    { key: 'VCASCA_08', label: 'Container 8', required: false },
    { key: 'VCASCA_09', label: 'Container 9', required: false },
    { key: 'VCASCA_10', label: 'Container 10', required: false },
  ];

  const handleChange = (field, value) => {
    setVehicleData({ ...vehicleData, [field]: value });
  };

  const handleClearContainer = (field) => {
    setVehicleData({ ...vehicleData, [field]: '' });
  };

  const handleAddContainer = () => {
    if (visibleContainersCount < 10) {
      setVisibleContainersCount(visibleContainersCount + 1);
    }
  };

  // Trouver le nombre de containers remplis
  const filledContainersCount = containers.filter(
    (container) => vehicleData[container.key]
  ).length;

  // Cas 1 : Citerne compartimentée - afficher les containers
  if (isCiterneCompartimentee) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div>
            <div className="tw-text-lg tw-font-semibold">
              Configuration des compartiments
            </div>
            <div className="tw-text-sm tw-text-gray-600 tw-mt-1">
              Définissez les capacités de chaque compartiment de la citerne.
            </div>
          </div>
          <div className="tw-bg-main tw-text-white tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-semibold">
            {filledContainersCount} compartiment{filledContainersCount > 1 ? 's' : ''}
          </div>
        </div>

        <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-4">
          {containers.slice(0, visibleContainersCount).map((container, index) => (
            <div key={container.key} className="tw-relative">
              <div className="tw-flex tw-items-end tw-gap-2">
                <div className="tw-flex-1">
                  <InputText
                    fieldTitle={`Compartiment ${index + 1} (capacité en kg)`}
                    value={vehicleData[container.key]}
                    setValue={(value) => handleChange(container.key, value)}
                    isRequired={index === 0}
                    placeholder="Ex: 1500"
                    type="number"
                    emptyError={
                      index === 0
                        ? 'Au moins un compartiment est requis'
                        : undefined
                    }
                  />
                </div>
                {index > 0 && (
                  <button
                    onClick={() => handleClearContainer(container.key)}
                    className="tw-mb-0 tw-p-2 tw-text-red-500 hover:tw-text-red-700 tw-transition-colors"
                    title="Supprimer ce compartiment"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            </div>
          ))}

          {visibleContainersCount < 10 && (
            <button
              type="button"
              onClick={handleAddContainer}
              className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-p-3 tw-border-2 tw-border-dashed tw-border-gray-300 tw-rounded-lg tw-text-gray-600 hover:tw-border-main hover:tw-text-main tw-transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Ajouter un compartiment</span>
            </button>
          )}
        </div>

        <div className="tw-mt-4 tw-p-4 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg">
          <div className="tw-flex tw-items-start tw-gap-3">
            <FontAwesomeIcon
              icon={faPlus}
              className="tw-text-blue-500 tw-mt-0.5"
            />
            <div className="tw-text-sm tw-text-blue-800">
              <strong>Astuce :</strong> Les compartiments représentent les
              différentes cuves de votre citerne. La capacité totale du véhicule
              sera calculée automatiquement en additionnant tous les compartiments.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Cas 2 : Autres types de véhicules - afficher un seul champ de capacité
  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div>
        <div className="tw-text-lg tw-font-semibold">
          Capacité du véhicule
        </div>
        <div className="tw-text-sm tw-text-gray-600 tw-mt-1">
          Définissez la capacité totale du véhicule en tonnes.
        </div>
      </div>

      <div className="tw-mt-4 tw-max-w-md">
        <InputText
          fieldTitle="Capacité totale (en tonnes)"
          value={vehicleData.VCASCA_01}
          setValue={(value) => handleChange('VCASCA_01', value)}
          isRequired={true}
          placeholder="Ex: 25"
          type="number"
          emptyError="La capacité est requise"
        />
      </div>

      <div className="tw-mt-4 tw-p-4 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg">
        <div className="tw-flex tw-items-start tw-gap-3">
          <FontAwesomeIcon
            icon={faPlus}
            className="tw-text-blue-500 tw-mt-0.5"
          />
          <div className="tw-text-sm tw-text-blue-800">
            <strong>Information :</strong> Pour un véhicule de type{' '}
            <strong>{vehicleData.LIBTYP}</strong>, une seule capacité totale est
            nécessaire.
          </div>
        </div>
      </div>
    </div>
  );
}

